import React from 'react';
import { loadComponent } from '../utils/load-component';
import { validateMfeInitConfig } from '../utils/validate-mfe-init-config';
function log(...args) {
    if (localStorage.getItem('mfe-utils-mfe-lifecycle-logger') === 'true') {
        console.log(...args);
    }
}
/**
 * The DynamicComponent is a React component that renders the specified MFE inside its self.
 * The key props to pass in are:
 *   remote: string; //Syncs up with ModuleFederation 'name'
 *   module: string; //Syncs up with ModuleFederation 'exposes'
 *   url: string; //This will normally be remoteEntry.js and is primarily used as a fallback url
 *
 * You can see an example of how to export an mfe in the ModuleFederation configuration in this file:
 * https://gitlab.com/powerschoolgroup/ui_ux/mfe-architecture/mfe-starter-pack/-/blob/develop/webpack.config.js?ref_type=heads
 */
export class DynamicComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {};
        this.containerRef = (element) => {
            var _a;
            log('DynamicComponent.containerRef Update');
            if (element == null) {
                return;
            }
            log('DynamicComponent.containerRef Update. Calling psMfeInit', element, this.props.locale, this.props.componentProps);
            const initResponse = (_a = this.state.initModule) === null || _a === void 0 ? void 0 : _a.psMfeInit(validateMfeInitConfig(Object.assign({ element: element, locale: this.props.locale || '', injectionRoute: this.props.injectionRoute }, this.props.componentProps)));
            if (initResponse != null) {
                initResponse.then((response) => {
                    this.setState({ unloadMfe: response.unloadMfe });
                    this.setState({ updateMfeConfig: response.updateMfeConfig });
                });
            }
        };
        this.createDynamicComponent();
    }
    //This has been tested using a class based component and it did work.
    createDynamicComponent() {
        if (this.props.remote != null && this.props.module != null && this.props.url != null) {
            log('DynamicComponent.createDynamicComponent() called', this.props);
            const loaderFunction = loadComponent(this.props.remote, 'default', this.props.module, this.props.url, this.props.throwErrors);
            loaderFunction().then((module) => {
                if (typeof (module === null || module === void 0 ? void 0 : module.default) === 'object') {
                    //If it's an object, and it has the property psMfeInit, then we know it uses an init function.
                    if (typeof module.default.psMfeInit === 'function') {
                        //We need to initialize the mfe by calling psMfeInit.
                        this.setState({ initModule: module.default });
                        log('DynamicComponent saving init function', module.default);
                    }
                }
                else if (typeof (module === null || module === void 0 ? void 0 : module.default) === 'function') {
                    //If it's a function, we expect that it's a react component function.
                    this.setState({ 'reactFunctionComponent': module.default });
                }
            }).catch((error) => {
                // We throw these errors in the set state callback so that the react error boundary package will be able to detect them
                // This is a workaround for the react-error-boundary package since it isn't built to use with class components
                // The errorMessage prop is only used for these specific errors so that we can throw an error during a react render
                // NOTE: This did not work in the actual render call in this file for an undetermined reason
                switch (error.message) {
                    case 'MODULE_ERROR':
                        if (this.props.throwErrors === true) {
                            this.setState({
                                errorMessage: 'MODULE_ERROR'
                            }, () => {
                                throw new Error(this.state.errorMessage);
                            });
                        }
                        break;
                    case 'URL_ERROR':
                        if (this.props.throwErrors === true) {
                            this.setState({
                                errorMessage: 'URL_ERROR'
                            }, () => {
                                throw new Error(this.state.errorMessage);
                            });
                        }
                        break;
                    case 'REMOTE_ERROR':
                        if (this.props.throwErrors === true) {
                            this.setState({
                                errorMessage: 'REMOTE_ERROR'
                            }, () => {
                                throw new Error(this.state.errorMessage);
                            });
                        }
                        break;
                    default:
                        console.error(error);
                        break;
                }
            });
        }
    }
    componentDidUpdate(previousProps) {
        if (this.props.url !== previousProps.url && this.props.remote === previousProps.remote) {
            console.warn('🚨🚨🚨🚨🚨🚨 DYNAMIC COMPONENT UPDATE 🚨🚨🚨🚨🚨🚨'
                + '\n' + '******* THE DYNAMIC COMPONENT URL, REMOTE, AND MODULE PROPS SHOULD BE UPDATED TO VALID VALUES AT THE SAME TIME ******'
                + '\n' + '******* IF THERE IS A LOADING FAILURE, TRY LOADING DYNAMICOMPONENT WITH THE VALUES BELOW ON INITIAL PAGE LOAD AND FOR MORE SPECIFIC ERROR LOGGING ******'
                + '\n' + 'URL: ' + this.props.url
                + '\n' + 'REMOTE: ' + this.props.remote
                + '\n' + 'MODULE: ' + this.props.module
                + '\n' + '🚨🚨🚨🚨🚨🚨 DYNAMIC COMPONENT UPDATE 🚨🚨🚨🚨🚨🚨');
        }
        const componentPropsChanged = JSON.stringify(this.props.componentProps) !== JSON.stringify(previousProps.componentProps);
        //Check to see if any of the props we cared about change
        if (this.props.remote !== previousProps.remote ||
            this.props.module !== previousProps.module ||
            this.props.url !== previousProps.url ||
            componentPropsChanged) {
            // New code to handle updateProps
            //If only the component props changed
            if (typeof this.state.updateMfeConfig === 'function') {
                if (this.props.remote === previousProps.remote &&
                    this.props.module === previousProps.module &&
                    this.props.url === previousProps.url &&
                    componentPropsChanged) {
                    const updatedConfig = validateMfeInitConfig(Object.assign({ element: this.containerRef.current, locale: this.props.locale || '', injectionRoute: this.props.injectionRoute }, this.props.componentProps));
                    this.state.updateMfeConfig(updatedConfig || {});
                    return; // Early return if only componentProps changed
                }
            }
            //Clear the old state
            this.setState({
                reactFunctionComponent: undefined,
                initModule: undefined
            });
            //When we clear the state, it's going to remove the element that the MFE is rendered into the DOM.
            //So we call the unload function here.
            this.unloadCurrentMfe();
            //setTimeout waits for React to finish running with the cleared state.
            //Once that's finished, we run createDynamicComponent().
            setTimeout(() => {
                this.createDynamicComponent();
            });
        }
    }
    unloadCurrentMfe() {
        if (this.state.unloadMfe != null) {
            this.state.unloadMfe();
        }
    }
    //This is a React lifecycle method
    componentWillUnmount() {
        this.unloadCurrentMfe();
    }
    render() {
        if (this.state.reactFunctionComponent != null) {
            const FederatedComponent = React.createElement(this.state.reactFunctionComponent, this.props.componentProps);
            return (React.createElement("dynamic-component", { "data-remote-url": this.props.url, "data-remote": this.props.remote, "data-module": this.props.module, class: this.props.className }, FederatedComponent));
        }
        if (this.state.initModule != null) {
            return React.createElement("dynamic-component", { "data-remote-url": this.props.url, "data-remote": this.props.remote, "data-module": this.props.module, class: this.props.className },
                React.createElement("div", { ref: this.containerRef }));
        }
        return (React.createElement(React.Fragment, null));
    }
}
